import {EditWorksiteModalProps} from "./types";
import React, {useContext, useState} from "react";
import CountryContext from "../../../CountryContext";
import {FormattedMessage, useIntl} from "react-intl";
import {useWorksites} from "@feature/worksites/hooks/useWorksites";
import getUserPermissions from "@utils/getUserPermissions";
import AuthenticationService from "@services/AuthenticationService";
import {NotificationsContext} from "@ui-components/Notifications";
import {PRIVILEGED_ROLES} from "../../../constants";
import Modal from "@ui-components/Modal";
import WorksiteTypeSelect from "@components/WorksiteTypeSelect";
import RadioGroup from "@ui-components/RadioGroup";
import {
  DEFAULT_WORKSITE_CONSTRUCTION_OPTIONS,
  DEFAULT_WORKSITE_STATUS_DATA,
  DEFAULT_WORKSITE_STATUS_OPTIONS
} from "@utils/constants";
import Select from "@ui-components/Select";
import _ from "lodash";
import moment from "moment";
import Input from "@ui-components/Input";
import {CITY_REGEX} from "@utils/regex";
import Toggle from "@ui-components/Toggle";
import types from "@ui-components/constants/status-types-config";
import {ExclamationCircleIcon} from "@heroicons/react/24/solid";
import {COUNTRY_CODES_MAPPINGS, WORKSITE_DESC_MAX_CHARS} from "./lib/consts";
import {WorksitesSchema} from "@feature/types";
import Button from "@ui-components/Button";

export const EditWorksiteModal = ({
  onSuccess,
  onClose,
  worksite,
  worksiteTypes,
}: EditWorksiteModalProps) => {

  // context
  const {country} = useContext(CountryContext);
  const {push} = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const {upsert, checkCountry, submitting} = useWorksites();

  // utils
  const perm = getUserPermissions(true);
  const userData = AuthenticationService.getUserData();
  const mode = !!worksite ? "edit" : "add";
  const showFlgTest = [...PRIVILEGED_ROLES, 'field'].includes(userData.role_type);
  const createStatusObject = (status: "1" | "2" | "3" | "4" | "5") => {
    const obj = _.clone(DEFAULT_WORKSITE_STATUS_DATA) as Record<"1" | "2" | "3" | "4" | "5", Record<string, any>>;
    obj[status]["flg_active"] = true;
    obj[status]["date"] = moment().unix();
    obj[status]["author"] = `${userData.firstname} ${userData.lastname}`;
    return obj;
  }

  // state
  const [values, setValues] = useState({
    address: worksite?.address || "",
    city: worksite?.city || "",
    name: !!worksite && worksite.des_worksite !== `${worksite.address}  - ${worksite.city}` ? worksite.des_worksite : "",
    worksiteType: worksite?.cod_worksite_type || undefined,
    constructionType: worksite?.cod_worksite_construction || undefined,
    status: "1" as "1" | "2" | "3" | "4" | "5",
    isTest: worksite?.flg_test || false,
  });
  const [errors, setErrors] = useState({
    name: false,
    city: false,
    worksiteType: false,
    constructionType: false,
    status: false,
  })
  const [bypassCountryCheck, setBypassCountryCheck] = useState(false);
  const [loading, setLoading] = useState(false);

  const _onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    setLoading(true);
    e.preventDefault();

    // Perform validation
    const actualName = values.name || `${values.address}  - ${values.city}`;
    const foundErrors = {
      name: actualName.length > WORKSITE_DESC_MAX_CHARS,
      city: !values.city || !CITY_REGEX.test(values.city),
      worksiteType: !values.worksiteType,  // Type must be selected
      constructionType: !values.constructionType,  // Construction must be selected
      status: !values.status,  // Status must be selected
    }
    if (Object.values(foundErrors).some(Boolean)) {
      // If any error is found, set the errors and return
      setErrors(foundErrors);
      setLoading(false);
      return;
    }

    try {

      // Check that the address country matches the platform country
      if (!bypassCountryCheck) {
        if (!(country.code in COUNTRY_CODES_MAPPINGS)) {
          // CASE A: country code not handled ---> send a console warning and continue
          console.warn(`unhandled country code ${country.code} - no checks will be performed`)
          setBypassCountryCheck(false);
        }
        else {
          const worksiteCountry = await checkCountry(values.address, values.city);
          if (COUNTRY_CODES_MAPPINGS[country.code] !== worksiteCountry) {
            // CASE B: country code different from address country ---> show a UI warning and stop
            setBypassCountryCheck(true);
            return;
          }
          else {
            // CASE C: country code equal to address country ---> do nothing
            setBypassCountryCheck(false);
          }
        }
      }

      // Perform upsert
      const obj: WorksitesSchema = {
        des_worksite: actualName,
        latitude: 0.0,
        longitude: 0,
        address: values.address,
        city: values.city,
        cod_province: "",  // Leave empty: will be filled by the backend
        des_province: "",  // Leave empty: will be filled by the backend
        des_state: "",  // Leave empty: will be filled by the backend
        cod_country: "",  // Leave empty: will be filled by the backend
        des_country: "",  // Leave empty: will be filled by the backend
        house_number: "",  // Leave empty: will be filled by the backend
        zip_code: "",  // Leave empty: will be filled by the backend
        cod_worksite_type: values.worksiteType,
        images_urls: worksite?.images_urls ?? {data: []},
        des_seismic_classification: "",
        des_climate_classification: "",
        cod_worksite_construction: values.constructionType,
        cod_worksite_status: mode === "edit"
          ? worksite!.cod_worksite_status
          : createStatusObject(values.status),
        flg_test: values.isTest,
        id_creator: worksite?.id_creator ?? userData.id,
        r_perm: worksite?.r_perm ?? perm,
        w_perm: worksite?.w_perm ?? perm,
        business: worksite?.business ?? userData.business,
      };
      const result = await upsert(worksite?.id ?? null, obj);

      // Show success message and return created/updated record
      push({
        title: intl.formatMessage({id: mode === "add" ? "worksite_created_successfully" : "worksite_updated_successfully"}),
        type: "success",
      });
      onSuccess(result);
    }
    catch (error) {
      console.error(error);
      push({
        title: intl.formatMessage({id: "server_error"}),
        type: "error",
      })
    } finally {
      setLoading(false);
    }
  };

  return (
    // @ts-ignore
    <Modal opened onExit={onClose}>
      <div className="flex flex-col gap-8">
        <h3 className="text-center text-2xl font-extrabold text-black-500">
          <FormattedMessage id="add_worksite"/>
        </h3>
        <form
          id="worksite-form"
          onSubmit={_onSubmit}
          onChange={() => setBypassCountryCheck(false)}
        >

          <WorksiteTypeSelect
            onClick={(type: string) => setValues((prev) => ({...prev, worksiteType: type}))}
            worksiteTypes={worksiteTypes ?? []}
            value={values.worksiteType}
          />

          {errors.worksiteType && (
            <p className="mt-1 text-xs text-red-600">
              <FormattedMessage id="worksite_type_selection_error" />
            </p>
          )}

          {/* @ts-ignore */}
          <RadioGroup
            id="cod_worksite_construction"
            title={intl.formatMessage({id: "worksite_construction"})}
            options={DEFAULT_WORKSITE_CONSTRUCTION_OPTIONS(intl)}
            currentValue={values.constructionType}
            onChange={(value: string) => setValues((prev) => ({...prev, constructionType: value}))}
            horizontal
          />
          {errors.constructionType && (
            <p className="mt-1 text-xs text-red-600">
              {intl.formatMessage({
                id: "worksite_construction_selection_error",
              })}
            </p>
          )}

          {mode === "add" && <>
            {/* @ts-ignore */}
            <Select
              options={DEFAULT_WORKSITE_STATUS_OPTIONS(intl)}
              value={values.status}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setValues((prev) => (
                  {...prev, status: e.target.value as "1" | "2" | "3" | "4" | "5"}
                ));
              }}
              label={intl.formatMessage({id: "worksite_status",})}
            />
            {errors.status && (
              <p className="mt-1 text-xs text-red-600">
                {intl.formatMessage({
                  id: "worksite_status_selection_error",
                })}
              </p>
            )}
          </>}

          {/* @ts-ignore */}
          <Input
            value={values.address}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValues((prev) => ({...prev, address: e.target.value}));
            }}
            label={intl.formatMessage({id: "address"})}
            errorMessage={intl.formatMessage({id: "enter_address"})}
            required
          />

          {/* @ts-ignore */}
          <Input
            value={values.city}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValues((prev) => ({...prev, city: e.target.value}));
            }}
            label={intl.formatMessage({id: "worksite_city"})}
            validation={(value: string) => CITY_REGEX.test(value)}
            errorMessage={intl.formatMessage({id: "invalid_city"})}
            required
          />

          {/* @ts-ignore */}
          <Input
            value={values.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValues((prev) => ({...prev, name: e.target.value}));
            }}
            label={intl.formatMessage({id: "worksite_name"})}
            placeholder={
              !!values.address && !!values.city
                ? `${values.address}  - ${values.city}`
                : ""
            }
          />
          {errors.constructionType && (
            <p className="mt-1 text-xs text-red-600">
              {intl.formatMessage({
                id: "worksite_construction_selection_error",
              })}
            </p>
          )}

          {showFlgTest && (
            // @ts-ignore
            <Toggle
              label={intl.formatMessage({id: "worksite_is_test"})}
              checked={values.isTest}
              onChange={() => setValues((prev) => ({...prev, isTest: !prev.isTest}))}
            />
          )}

          {
            bypassCountryCheck && (
              <div className={`py-3 px-4 ${types.warning.classes.regular} rounded-lg shadow`}>
                <h2 className={`flex items-center gap-2 text-lg ${types.warning.classes.textBold}`}>
                  <ExclamationCircleIcon className="w-6"/>
                  <FormattedMessage id="warning"/>
                </h2>
                <p className={`mt-1 ${types.warning.classes.textBold}`}>
                  <FormattedMessage
                    id="worksites_country_check_text"
                    values={{
                      countryName: country.name,
                      buttonText: intl.formatMessage({id: mode}).toUpperCase()
                    }}
                  />
                </p>
              </div>
            )
          }

          <div className="flex items-end justify-end mt-4">
            {/* @ts-ignore */}
            <Button submitting={loading} type="submit">
              <FormattedMessage id={mode}/>
            </Button>
          </div>

        </form>
      </div>
    </Modal>
  );

}